import type { FC, Ref } from "react";
import { mergeClasses } from "@huub-web/utils";
import {
  Card,
  type CardProps,
  Flex,
  Indicator,
  Text,
  type IndicatorProps,
} from "@huub-web/ui";

import {
  amenityCardStyle,
  cardTitleStyle,
  cardSubtitleStyle,
  cardContentStyle,
} from "./AmenityCard.css";

export type AmenityCardProps = CardProps & {
  /**
   * Icon to display in the indicator
   */
  icon?: IndicatorProps["icon"];
  /**
   * Name of the amenity
   */
  name: string;
  /**
   * Distance to the amenity (e.g. "500m")
   */
  distance?: string;
  /**
   * Type of amenity (e.g. "Restaurant")
   */
  type?: string;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * AmenityCard displays an amenity with an icon, title, and subtitle.
 * Simplified card component for neighbourhood amenity listings.
 */
export const AmenityCard: FC<AmenityCardProps> = ({
  icon = "check",
  name,
  distance,
  type,
  className,
  ref,
  ...props
}) => {
  const subtitle = (() => {
    if (distance && type) {
      return `${distance} • ${type}`;
    }
    return distance || type;
  })();

  return (
    <Card
      ref={ref}
      className={mergeClasses(amenityCardStyle, className)}
      contentClassName={cardContentStyle}
      edgeDirection="horizontal"
      {...props}
    >
      <Indicator
        icon={icon}
        variant="tertiarySurface"
        type="square"
        size="inline"
      />
      <Flex direction="column" gap="xxs" grow>
        <Text as="h3" bold className={cardTitleStyle} clamp={2}>
          {name}
        </Text>
        {subtitle && (
          <Text
            variant="bodyS"
            color="currentColor50"
            className={cardSubtitleStyle}
          >
            {subtitle}
          </Text>
        )}
      </Flex>
    </Card>
  );
};
